/*eslint  @typescript-eslint/no-use-before-define: 0*/
import _ from "lodash";
import i18n from "@/plugins/i18n";
import { DataOptions } from "vuetify";
import { defineModule } from "direct-vuex";
import { RELOAD_TYPES } from "@/static/reloadTypes";
import { SelectItem } from "@/models/api/SelectItem";
import { MESSAGE_TYPES } from "@/static/messageTypes";
import { filterCapitalize } from "@/filters/capitalize";
import BaseCommand from "@/models/api/commands/BaseCommand";
import { FORM_OF_PAYMENT_TYPE } from "@/static/formOfPaymentType";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { AlertModelInterface } from "@/models/components/popup/Alert";
import { AttributeItem } from "@/models/api/bookings/BookingGetTemporaryItem";
import { FileViewModel } from "@/models/api/costDocumentsOther/editor/FileViewModel";
import CostDocumentsOtherEditorCommandsModule from "@/store/modules/costDocumentsOther/commands";
import { ContractorViewModel } from "@/models/api/costDocumentsOther/editor/ContractorViewModel";
import { CheckBeforeSaveCommandResponse, OperationResult } from "@/models/api/orders/OperationResult";
import { CostDocumentOtherViewModel } from "@/models/api/costDocumentsOther/editor/CostDocumentOtherViewModel";
import { CostDocumentOtherItemViewModel } from "@/models/api/costDocumentsOther/editor/CostDocumentOtherItemViewModel";
import {
  getTemporaryFiles,
  addCostDocumentOther,
  saveCostDocumentOther,
  editCostDocumentOther,
  getTemporaryAttributes,
  getTemporaryContractor,
  postUploadTemporaryFiles,
  getTemporaryCostDocumentOther,
  updateCostDocumentOtherTemporary,
  getTemporaryCostDocumentOtherItems,
  getCostDocumentOtherTypes,
  getBankAccounts,
  getPayerBankAccounts,
} from "@/requests/costDocumentOther";
import routerInstance from "@/plugins/router";
import { FILTER_NAMES } from "@/static/filterNames";
import { getUserCreditCards } from "@/requests/creditCard";
import { SERVICE_CATEGORY } from "@/static/serviceCategory";
import { getServiceTypesSimple } from "@/requests/serviceType";
import { COST_DOCUMENT_TYPES } from "@/static/costDocumentTypes";
import { CONTRACTOR_TYPE_ENUM } from "@/static/contractorTypeEnum";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import { ContractComponent, ContractComponentResponse } from "@/models/api/contracts/ContractComponent";
import { ContractByContractor, ContractByContractorResponse } from "@/models/api/contracts/ContractByContractor";
import { getComponentsForCostDocument, getContractComponents, getContractsByContractor } from "@/requests/contract";

export interface CostDocumentsOtherEditorStateInterface {
  sessionId: string | null;
  operationId: string | null;
  type: COST_DOCUMENT_TYPES;
  costDocumentId: string | null;
  costDocumentItemId: string | null;
  costDocument: CostDocumentOtherViewModel | null;
  costDocumentItems: Array<CostDocumentOtherItemViewModel>;
  contractor: ContractorViewModel | null;
  attributes: Array<AttributeItem>;
  files: Array<FileViewModel>;
  payerBankAccounts: Array<string>;

  commandQueue: Array<BaseCommand>;
  isQueueProcessing: boolean;
  isAltered: boolean;

  serviceTypes: Array<SelectItem>;
  documentTypes: Array<SelectItem>;
  selectedFile: FileViewModel | null;

  tableHeight: number;

  isComponentsDialog: boolean;
  contractsList: Array<ContractByContractor>;
  contractsCount: number;
  contractsOptions: DataOptions;
  contractsFilters: Array<CriterionItemRequest> | null;
  selectedContract: ContractByContractor | null;
  isContractsLoading: boolean;
  currentContractorType: CONTRACTOR_TYPE_ENUM;

  componentsList: Array<ContractComponent>;
  componentsCount: number;
  componentsOptions: DataOptions;
  componentsFilters: Array<CriterionItemRequest> | null;
  selectedComponent: ContractComponent | null;
  isComponentsLoading: boolean;

  pairedList: Array<ContractComponent>;
  pairedCount: number;
  pairedOptions: DataOptions;
  pairedFilters: Array<CriterionItemRequest> | null;
  isPairedLoading: boolean;
  bankAccountsList: Array<SelectItem>;
  userCreditCards: Array<SelectItem>;

  openCostDocumentAttributes: boolean;
  attributeTrigger: boolean;
}

export const CostDocumentsOtherEditorState: CostDocumentsOtherEditorStateInterface = {
  sessionId: null,
  operationId: null,
  type: COST_DOCUMENT_TYPES.Other,
  costDocumentId: null,
  costDocumentItemId: null,
  costDocument: null,
  costDocumentItems: [],
  contractor: null,
  attributes: [],
  files: [],
  payerBankAccounts: [],

  commandQueue: [],
  isQueueProcessing: false,
  isAltered: false,

  serviceTypes: [],
  documentTypes: [],
  selectedFile: null,

  tableHeight: 600,

  isComponentsDialog: false,
  contractsList: [],
  contractsCount: 0,
  contractsOptions: new DefaultDataOptions(),
  contractsFilters: null,
  selectedContract: null,
  isContractsLoading: false,
  currentContractorType: CONTRACTOR_TYPE_ENUM.Seller,

  componentsList: [],
  componentsCount: 0,
  componentsOptions: new DefaultDataOptions(),
  componentsFilters: null,
  selectedComponent: null,
  isComponentsLoading: false,

  pairedList: [],
  pairedCount: 0,
  pairedOptions: new DefaultDataOptions(),
  pairedFilters: null,
  isPairedLoading: false,
  bankAccountsList: [],
  userCreditCards: [],

  openCostDocumentAttributes: false,
  attributeTrigger: false,
};

const CostDocumentsOtherEditorModule = defineModule({
  modules: {
    commands: CostDocumentsOtherEditorCommandsModule,
  },
  state: CostDocumentsOtherEditorState as CostDocumentsOtherEditorStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setSessionId(state, param: string | null) {
      state.sessionId = param;
    },
    setOperationId(state, param: string | null) {
      state.operationId = param;
    },
    setType(state, param: COST_DOCUMENT_TYPES) {
      state.type = param;
    },
    setCostDocumentId(state, param: string | null) {
      state.costDocumentId = param;
    },
    setCostDocumentItemId(state, param: string | null) {
      state.costDocumentItemId = param;
      state.openCostDocumentAttributes = false;
      state.attributeTrigger = !state.attributeTrigger;
    },
    setCostDocument(state, param: CostDocumentOtherViewModel | null) {
      state.costDocument = param;
    },
    setCostDocumentItems(state, param: Array<CostDocumentOtherItemViewModel>) {
      state.costDocumentItems = param;
    },
    setContractor(state, param: ContractorViewModel | null) {
      state.contractor = param;
    },
    setAttributes(state, param: Array<AttributeItem>) {
      state.attributes = param;
    },
    setFiles(state, param: Array<FileViewModel>) {
      state.files = param;
    },
    setPayerBankAccount(state, param: Array<string>) {
      state.payerBankAccounts = param;
    },

    setIsQueueProcessing(state, param: boolean) {
      state.isQueueProcessing = param;
    },
    setIsAltered(state, param: boolean) {
      state.isAltered = param;
    },

    setServiceTypes(state, param: Array<SelectItem>) {
      state.serviceTypes = param;
    },
    setDocumentTypes(state, param: Array<SelectItem>) {
      state.documentTypes = param;
    },
    setSelectedFile(state, param: FileViewModel | null) {
      state.selectedFile = param;
    },
    setTableHeight(state, param: number) {
      state.tableHeight = param;
    },

    setIsComponentsDialog(state, param: boolean) {
      state.isComponentsDialog = param;
    },
    setContractsList(state, param: ContractByContractorResponse) {
      state.contractsList = param.items;
      state.contractsCount = param.totalCount;
    },
    setContractsOptions(state, param: DataOptions) {
      state.contractsOptions = JSON.parse(JSON.stringify(param));
    },
    setContractsFilters(state, param: Array<CriterionItemRequest>) {
      state.contractsFilters = param;
    },
    setSelectedContract(state, param: ContractByContractor | null) {
      state.selectedContract = param;
    },
    setIsContractsLoading(state, param: boolean) {
      state.isContractsLoading = param;
    },
    setCurrentContractorType(state, param: CONTRACTOR_TYPE_ENUM) {
      state.currentContractorType = param;
    },

    setComponentsList(state, param: ContractComponentResponse) {
      state.componentsList = param.items;
      state.componentsCount = param.totalCount;
    },
    setComponentsOptions(state, param: DataOptions) {
      state.componentsOptions = JSON.parse(JSON.stringify(param));
    },
    setComponentsFilters(state, param: Array<CriterionItemRequest>) {
      state.componentsFilters = param;
    },
    setSelectedComponent(state, param: ContractComponent | null) {
      state.selectedComponent = param;
    },
    setIsComponentsLoading(state, param: boolean) {
      state.isComponentsLoading = param;
    },

    setPairedList(state, param: ContractComponentResponse) {
      state.pairedList = param.items;
      state.pairedCount = param.totalCount;
    },
    setPairedOptions(state, param: DataOptions) {
      state.pairedOptions = JSON.parse(JSON.stringify(param));
    },
    setPairedFilters(state, param: Array<CriterionItemRequest>) {
      state.pairedFilters = param;
    },
    setIsPairedLoading(state, param: boolean) {
      state.isPairedLoading = param;
    },
    setBankAccounts(state, param: Array<SelectItem>) {
      state.bankAccountsList = param;
    },
    setCostDocumentDescription(state, param: string) {
      if (state.costDocument != null) state.costDocument.description = param;
    },
    setUserCreditCards(state, param: Array<SelectItem>) {
      state.userCreditCards = param;
    },
    setOpenCostDocumentAttributes(state, param: boolean) {
      state.openCostDocumentAttributes = param;
      state.costDocumentItemId = null;
      state.attributeTrigger = !state.attributeTrigger;
    },
  },
  actions: {
    async clear(context) {
      const { commit } = _moduleActionContext(context);
      commit.setIsAltered(false);
      commit.setSessionId(null);
      commit.setOperationId(null);
      commit.setType(COST_DOCUMENT_TYPES.Other);
      commit.setCostDocumentId(null);
      commit.setCostDocument(null);
      commit.setCostDocumentItems([]);
      commit.setContractor(null);
      commit.setAttributes([]);
      commit.setFiles([]);
      commit.setSelectedFile(null);
      commit.setSelectedContract(null);
      commit.setSelectedComponent(null);
      commit.setOpenCostDocumentAttributes(false);
      commit.setCurrentContractorType(CONTRACTOR_TYPE_ENUM.Seller);
    },
    async addCostDocument(context, type: COST_DOCUMENT_TYPES) {
      const { commit, dispatch, state } = _moduleActionContext(context);
      await dispatch.clear();
      commit.setType(type);
      const session = await addCostDocumentOther(state.type);
      commit.setSessionId(session);
      commit.setOperationId(session);
      await dispatch.downloadAll();
    },
    async editCostDocument(context, param: { costDocumentId: string; type: COST_DOCUMENT_TYPES }) {
      const { commit, dispatch } = _moduleActionContext(context);
      await dispatch.clear();
      commit.setType(param.type);
      const session = await editCostDocumentOther(param.costDocumentId);
      commit.setSessionId(session);
      commit.setOperationId(session);
      commit.setCostDocumentId(param.costDocumentId);
      await dispatch.downloadAll();
    },
    async downloadAll(context) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      await Promise.all([
        rootDispatch.user.downloadOfficeSettings(),
        rootDispatch.user.downloadOfficeFormOfPayments(FORM_OF_PAYMENT_TYPE.CostDocumentOther),
        rootDispatch.attributes.downloadPrototypes(),
        dispatch.downloadDocumentTypes(),
        dispatch.downloadServiceTypes(),
      ]);
      await Promise.all([
        dispatch.getTemporaryCostDocument(),
        dispatch.getTemporaryCostDocumentItems(),
        dispatch.getTemporaryContractor(),
        dispatch.getTemporaryFiles(),
        dispatch.getPayerBankAccount(),
      ]);
    },
    async getContracts(context) {
      const { commit, state, rootState } = _moduleActionContext(context);
      if (state.contractor && state.contractor.importedContractorId) {
        commit.setIsContractsLoading(true);
        const response = await getContractsByContractor(
          {
            count: state.contractsOptions.itemsPerPage,
            page: state.contractsOptions.page - 1,
            criterions: _.cloneDeep(state.contractsFilters),
            filterName: rootState.costDocumentsOther.contractorContractFilterName,
          },
          state.contractor.importedContractorId
        );
        commit.setContractsList(response);
        commit.setIsContractsLoading(false);
        if (response.items.length > 0) commit.setSelectedContract(response.items[0]);
      }
    },
    async getComponents(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedContract) {
        commit.setIsComponentsLoading(true);
        const response = await getContractComponents(
          {
            count: state.componentsOptions.itemsPerPage,
            page: state.componentsOptions.page - 1,
            criterions: _.cloneDeep(state.componentsFilters),
            filterName: FILTER_NAMES.ContractComponentStandardFilter,
          },
          state.selectedContract.id
        );
        commit.setComponentsList(response);
        commit.setIsComponentsLoading(false);
        commit.setSelectedComponent(null);
      }
    },
    async getComponentsForCostDocument(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.costDocument) {
        commit.setIsPairedLoading(true);
        const response = await getComponentsForCostDocument(
          {
            count: state.pairedOptions.itemsPerPage,
            page: state.pairedOptions.page - 1,
            criterions: _.cloneDeep(state.pairedFilters),
            filterName: FILTER_NAMES.ContractComponentStandardFilter,
          },
          state.costDocument.contractComponentId
        );
        commit.setPairedList(response);
        commit.setIsPairedLoading(false);
      }
    },
    async downloadDocumentTypes(context) {
      const { commit } = _moduleActionContext(context);
      commit.setDocumentTypes(await getCostDocumentOtherTypes());
    },
    async downloadServiceTypes(context) {
      const { commit } = _moduleActionContext(context);
      commit.setServiceTypes(await getServiceTypesSimple(SERVICE_CATEGORY.Cost));
    },
    async getTemporaryCostDocument(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.operationId) commit.setCostDocument(await getTemporaryCostDocumentOther(state.operationId));
    },
    async getTemporaryCostDocumentItems(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.operationId) commit.setCostDocumentItems(await getTemporaryCostDocumentOtherItems(state.operationId));
    },
    async getTemporaryFiles(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.operationId) commit.setFiles(await getTemporaryFiles(state.operationId));
    },
    async getTemporaryContractor(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.operationId)
        commit.setContractor(await getTemporaryContractor(state.operationId, state.currentContractorType));
    },
    async getTemporaryAttributes(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.operationId)
        commit.setAttributes(await getTemporaryAttributes(state.operationId, state.costDocumentItemId));
    },
    async getPayerBankAccount(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.operationId) {
        commit.setPayerBankAccount(await getPayerBankAccounts(state.operationId));
      }
    },
    async addFiles(context, files: Array<File>) {
      const { dispatch, state } = _moduleActionContext(context);
      if (state.operationId)
        await dispatch.processResponse(await postUploadTemporaryFiles(state.operationId as string, files));
    },
    async addCommandToQueue(context, command: BaseCommand) {
      const { state, dispatch } = _moduleActionContext(context);
      state.commandQueue.push(command);
      if (!state.isQueueProcessing) {
        await dispatch.processCommandQueue();
      }
    },
    async getUserCreditCards(context) {
      const { commit } = _moduleActionContext(context);
      const response = await getUserCreditCards();
      commit.setUserCreditCards(response);
    },
    async getBankAccounts(context) {
      const { commit } = _moduleActionContext(context);
      commit.setBankAccounts(await getBankAccounts());
    },
    async processCommandQueue(context) {
      const { commit, state, dispatch } = _moduleActionContext(context);
      if (state.commandQueue.length) {
        commit.setIsQueueProcessing(true);
        try {
          const command = state.commandQueue.shift() as BaseCommand;
          command.SessionId = state.sessionId as string;
          command.OperationId = state.operationId as string;
          const response = await updateCostDocumentOtherTemporary(command);
          await dispatch.processResponse(response);
        } finally {
          commit.setIsQueueProcessing(false);
          if (state.commandQueue.length) dispatch.processCommandQueue(); // noinspection ES6MissingAwait
        }
      }
    },
    async processResponse(context, param: OperationResult) {
      const { dispatch, commit } = _moduleActionContext(context);
      commit.setOperationId(param.operationId);
      commit.setIsAltered(true);
      if (param.commandResponse)
        switch (param.commandResponse.constructor) {
          case CheckBeforeSaveCommandResponse:
            await dispatch.save(param.commandResponse as CheckBeforeSaveCommandResponse);
            break;
        }
      await dispatch.reload(param.reload);
    },
    async reload(context, param: Array<RELOAD_TYPES>) {
      const { dispatch } = _moduleActionContext(context);
      for (const reload of param)
        switch (reload) {
          case RELOAD_TYPES.Files:
            await dispatch.getTemporaryFiles();
            break;
          case RELOAD_TYPES.Contractor:
            await dispatch.getTemporaryContractor();
            break;
          case RELOAD_TYPES.Details:
            await dispatch.getTemporaryCostDocument();
            break;
          case RELOAD_TYPES.Items:
            await dispatch.getTemporaryCostDocumentItems();
            break;
          case RELOAD_TYPES.Attributes:
            await dispatch.getTemporaryAttributes();
            break;
          case RELOAD_TYPES.PayerBankAccounts:
            await dispatch.getPayerBankAccount();
        }
    },
    async save(context, param: CheckBeforeSaveCommandResponse | null = null) {
      const { rootDispatch, dispatch, commit, state } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (state.operationId) {
        if (param && param.isEditingLocked) {
          if (param.hasPermission) {
            const isConfirmed = (await rootDispatch.popup.addConfirmWithMessage(
              filterCapitalize(i18n.t("confirmSaveCostDocument"))
            )) as boolean;
            if (!isConfirmed) return;
          } else {
            await rootDispatch.popup.addAlert({
              title: filterCapitalize(i18n.t("error")),
              content: filterCapitalize(i18n.t("noPermissionSaveCostDocumentOther")),
              type: MESSAGE_TYPES.Error,
            } as AlertModelInterface);
            return;
          }
        }
        await saveCostDocumentOther(state.operationId);
        await rootDispatch.popup.addSavedOkSnackbar();
        commit.setIsAltered(false);
        if (state.costDocumentId) {
          switch (state.type) {
            case COST_DOCUMENT_TYPES.Other:
              await routerInstance.push({ name: "Costs" });
              break;
            case COST_DOCUMENT_TYPES.Commercial:
              await routerInstance.push({ name: "Commercial costs" });
              break;
          }
        } else await dispatch.addCostDocument(state.type);
      }
    },
  },
});

export default CostDocumentsOtherEditorModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, CostDocumentsOtherEditorModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, CostDocumentsOtherEditorModule);
